import React, { useState, useEffect, useRef, useMemo } from 'react';
import { libraryAPI, tagsAPI } from '../api';
import { getTranslation } from '../i18n';

// Tiny interpolation helper for i18n strings with {placeholder} tokens.
const fmt = (s, vars = {}) =>
  Object.keys(vars).reduce((acc, k) => acc.replaceAll(`{${k}}`, vars[k]), s);

// Mobile breakpoint matches the rest of the Library — narrow screens
// get a near-fullscreen sheet instead of a centered modal.
const NARROW = 760;

// Grid density presets. The minimum column width drives `repeat(auto-fill,
// minmax(N, 1fr))`, so smaller values mean more thumbnails per row — and
// fewer scroll-jumps when browsing big libraries.
const DENSITY = {
  comfortable: { minPx: 140, gap: 10, key: 'comfortable' },
  compact: { minPx: 95, gap: 8, key: 'compact' },
  tiny: { minPx: 64, gap: 6, key: 'tiny' },
};

const LibraryModal = ({ onClose, onSelect, sectionLabels = [], lang = 'en' }) => {
  // Resolved translation bundle for the active workspace language.
  const t = getTranslation(lang);

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sectionLabel, setSectionLabel] = useState('');

  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [counts, setCounts] = useState({ total_all: 0, total_uploaded: 0, total_external: 0 });

  const [filter, setFilter] = useState(null);

  const [allTags, setAllTags] = useState([]);
  const [activeTags, setActiveTags] = useState([]);

  // Mobile-only collapsible — tags hide behind a "Filters" toggle by
  // default so the visible header stays calm. Always-on for desktop.
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < NARROW : false
  );
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Density toggle — phones default to compact for higher image
  // density (Pinterest-style); desktop keeps the original comfortable
  // grid so existing workflows are unchanged.
  const [density, setDensity] = useState(() => {
    if (typeof window === 'undefined') return 'comfortable';
    return window.innerWidth < NARROW ? 'compact' : 'comfortable';
  });

  // Full-size preview overlay — opened either by tapping the small
  // magnifier glyph on a card or by long-pressing the thumbnail. This
  // lets the founder evaluate similar shots from a session before
  // committing them to the board's selection.
  const [previewImage, setPreviewImage] = useState(null);
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);
  const LONG_PRESS_MS = 480;

  const openPreview = (img) => setPreviewImage(img);
  const closePreview = () => setPreviewImage(null);

  const sentinelRef = useRef(null);
  const gridContainerRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsNarrow(window.innerWidth < NARROW);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    fetchTags();
  }, []);

  // Body scroll-lock while the sheet is open so the underlying board
  // doesn't pan around when the founder scrolls the grid on iOS.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    fetchLibrary(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, activeTags]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingMore]);

  const fetchTags = async () => {
    try {
      const { data } = await tagsAPI.getAll();
      setAllTags(data.tags || []);
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  };

  const fetchLibrary = async (reset = true) => {
    try {
      if (reset) {
        setLoading(true);
        setCurrentPage(1);
      }
      const tagParam = activeTags.length > 0 ? activeTags.join(',') : null;
      const { data } = await libraryAPI.list(filter, 1, 60, tagParam);
      const list = Array.isArray(data) ? data : Array.isArray(data?.images) ? data.images : [];
      setImages(list);
      setHasMore(data?.has_more || false);
      setCounts(data?.counts || { total_all: 0, total_uploaded: 0, total_external: 0 });
      setCurrentPage(1);
    } catch (err) {
      console.error('Error fetching library:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const tagParam = activeTags.length > 0 ? activeTags.join(',') : null;
      const { data } = await libraryAPI.list(filter, nextPage, 60, tagParam);
      const newImages = Array.isArray(data?.images) ? data.images : [];
      setImages(prev => {
        const existingIds = new Set(prev.map(img => img.id));
        const uniqueNewImages = newImages.filter(img => !existingIds.has(img.id));
        return [...prev, ...uniqueNewImages];
      });
      setHasMore(data?.has_more || false);
      setCounts(data?.counts || counts);
      setCurrentPage(nextPage);
    } catch (err) {
      console.error('Error loading more:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleSelection = (imageId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(images.map(img => img.id)));
  const clearSelection = () => setSelectedIds(new Set());

  const handleAdd = () => {
    if (selectedIds.size > 0) onSelect(Array.from(selectedIds), sectionLabel || null);
  };

  const toggleTag = (tag) => {
    setActiveTags(prev => prev.includes(tag) ? prev.filter(x => x !== tag) : [...prev, tag]);
  };

  const clearTagFilters = () => setActiveTags([]);

  const uploadedCount = counts.total_uploaded || 0;
  const externalCount = counts.total_external || 0;
  const allCount = counts.total_all || 0;

  const activeFilterCount = (filter ? 1 : 0) + activeTags.length;

  // Pre-compute the grid template once per density change. Empty deps on
  // `density` only — values are static lookups in DENSITY.
  const gridStyle = useMemo(() => {
    const d = DENSITY[density] || DENSITY.comfortable;
    return {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(${d.minPx}px, 1fr))`,
      gap: `${d.gap}px`,
      paddingRight: isNarrow ? '0' : '8px',
    };
  }, [density, isNarrow]);

  // ── Container layout ───────────────────────────────────────────────
  // Mobile: full-bleed sheet with sticky header + sticky action footer,
  // grid scrolls between them. Desktop: original centered modal.
  const containerStyle = isNarrow ? {
    position: 'fixed', inset: 0,
    backgroundColor: 'var(--kahla-white)',
    display: 'flex', flexDirection: 'column',
    zIndex: 1000,
    padding: 0,
    maxWidth: 'none',
    maxHeight: 'none',
    width: '100vw',
    height: '100vh',
    transform: 'none',
    margin: 0,
    borderRadius: 0,
    boxShadow: 'none',
    border: 'none',
    overflow: 'hidden',
  } : {
    maxWidth: '950px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1000,
  };

  // Sticky header background — kept on the same off-white so the
  // sheet feels continuous, not stacked.
  const stickyBg = 'var(--kahla-white)';

  // Render a single horizontally-scrollable row of filter chips on
  // mobile, with the active state painted directly on the chip. This
  // replaces the wrapped grid that took 2-3 rows of vertical space.
  const filterChips = (
    <div
      data-testid="library-modal-filter-strip"
      style={{
        display: 'flex',
        gap: '8px',
        flexWrap: isNarrow ? 'nowrap' : 'wrap',
        overflowX: isNarrow ? 'auto' : 'visible',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: isNarrow ? '4px' : 0,
        scrollbarWidth: 'none',
      }}
    >
      {[
        { key: null, label: `${t.lib_filter_all} (${allCount})`, testid: 'library-modal-filter-all' },
        { key: 'uploaded', label: `${t.lib_filter_uploaded} (${uploadedCount})`, testid: 'library-modal-filter-uploaded' },
        { key: 'external', label: `${t.lib_filter_external} (${externalCount})`, testid: 'library-modal-filter-external' },
        { key: 'used', label: t.lib_filter_used, testid: 'library-modal-filter-used' },
        { key: 'unused', label: t.lib_filter_unused, testid: 'library-modal-filter-unused' },
      ].map((f) => {
        const active = filter === f.key;
        return (
          <button
            key={String(f.key)}
            data-testid={f.testid}
            className={`btn-secondary ${active ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
            style={{
              backgroundColor: active ? 'var(--kahla-charcoal)' : 'transparent',
              color: active ? 'var(--kahla-white)' : 'var(--kahla-charcoal)',
              padding: '6px 14px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {f.label}
          </button>
        );
      })}
    </div>
  );

  const tagBar = allTags.length > 0 ? (
    <div style={{
      display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center',
    }}>
      <span style={{
        fontSize: '11px', color: 'var(--kahla-text-light)',
        letterSpacing: '0.05em', textTransform: 'uppercase', marginRight: '4px',
      }}>
        {t.lib_tags_label}
      </span>
      {activeTags.length > 0 && (
        <button
          onClick={clearTagFilters}
          data-testid="library-modal-clear-tags"
          style={{
            padding: '2px 8px', fontSize: '10px', cursor: 'pointer',
            border: '1px solid var(--kahla-border)', background: 'transparent',
            color: 'var(--kahla-text-light)',
          }}
        >
          {t.lib_clear_all}
        </button>
      )}
      {allTags.map(tag => {
        const isActive = activeTags.includes(tag);
        return (
          <button
            key={tag}
            onClick={() => toggleTag(tag)}
            data-testid={`library-modal-tag-${tag}`}
            style={{
              padding: '2px 10px', fontSize: '11px', cursor: 'pointer',
              border: isActive ? '1px solid var(--kahla-charcoal)' : '1px solid var(--kahla-border)',
              background: isActive ? 'var(--kahla-charcoal)' : 'var(--kahla-cream)',
              color: isActive ? 'var(--kahla-white)' : 'var(--kahla-text)',
            }}
          >
            {tag}
          </button>
        );
      })}
      {activeTags.length > 1 && (
        <span style={{ fontSize: '10px', color: 'var(--kahla-text-light)', fontStyle: 'italic' }}>
          {t.lib_tags_all_hint}
        </span>
      )}
    </div>
  ) : null;

  // Density toggle pill — three editorial chips. Visible on every
  // viewport so power users can also bump density on desktop.
  const densityToggle = (
    <div
      data-testid="library-modal-density-toggle"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexShrink: 0,
      }}
    >
      <span style={{
        fontSize: '10px',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: 'var(--kahla-text-light)',
      }}>
        {t.lm_density_label}
      </span>
      {[
        { k: 'comfortable', label: t.lm_density_comfortable },
        { k: 'compact', label: t.lm_density_compact },
        { k: 'tiny', label: t.lm_density_tiny },
      ].map(({ k, label }) => {
        const active = density === k;
        return (
          <button
            key={k}
            data-testid={`library-modal-density-${k}`}
            onClick={() => setDensity(k)}
            style={{
              padding: '4px 10px',
              fontSize: '11px',
              cursor: 'pointer',
              border: '1px solid var(--kahla-border)',
              background: active ? 'var(--kahla-charcoal)' : 'transparent',
              color: active ? 'var(--kahla-white)' : 'var(--kahla-text)',
              transition: 'background-color 0.18s, color 0.18s',
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} style={{ zIndex: 999 }}></div>
      <div
        className="modal-content fade-in"
        data-testid="library-modal"
        style={containerStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Sticky Header ─────────────────────────────────────────── */}
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          background: stickyBg,
          padding: isNarrow ? '14px 18px 10px' : '0 0 8px',
          borderBottom: isNarrow ? '1px solid var(--kahla-border)' : 'none',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
          }}>
            <div style={{ minWidth: 0, flex: 1, textAlign: isNarrow ? 'left' : 'center' }}>
              <h2 style={{
                fontFamily: 'Playfair Display, serif',
                fontSize: isNarrow ? '20px' : '24px',
                margin: 0,
                lineHeight: 1.2,
              }}>
                {t.lm_title}
              </h2>
              <p style={{
                fontSize: '12px',
                color: 'var(--kahla-text-light)',
                margin: '4px 0 0',
                fontStyle: 'italic',
                fontFamily: '"Cormorant Garamond", serif',
              }}>
                {t.lm_subtitle}
              </p>
            </div>
            {isNarrow && (
              <button
                onClick={onClose}
                data-testid="library-modal-close-btn"
                aria-label={t.lm_close_aria}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '24px',
                  color: 'var(--kahla-text-light)',
                  lineHeight: 1,
                  padding: '0 4px',
                }}
              >
                ×
              </button>
            )}
          </div>

          {/* Mobile: collapsible filter / tag block triggered by a small
              "Filters" pill so the visible chrome stays minimal. */}
          {isNarrow && (
            <div style={{ marginTop: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setFiltersOpen((p) => !p)}
                  data-testid="library-modal-toggle-filters"
                  style={{
                    padding: '4px 12px',
                    fontSize: '11px',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    border: '1px solid var(--kahla-border)',
                    background: filtersOpen ? 'var(--kahla-cream)' : 'transparent',
                    color: 'var(--kahla-text)',
                  }}
                >
                  {t.lm_filters_btn}
                  {activeFilterCount > 0 && (
                    <span style={{
                      marginLeft: 6,
                      fontSize: 10,
                      background: 'var(--kahla-charcoal)',
                      color: 'var(--kahla-white)',
                      borderRadius: 999,
                      padding: '0 6px',
                    }}>{activeFilterCount}</span>
                  )}
                </button>
                {densityToggle}
              </div>
              {filtersOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
                  {filterChips}
                  {tagBar}
                </div>
              )}
            </div>
          )}

          {/* Desktop: always-visible filters under the title. */}
          {!isNarrow && (
            <>
              <div style={{
                marginTop: '12px',
                borderBottom: '1px solid var(--kahla-border)',
                paddingBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
              }}>
                {filterChips}
                {densityToggle}
              </div>
              {tagBar && (
                <div style={{ marginTop: '10px' }}>{tagBar}</div>
              )}
            </>
          )}
        </div>

        {/* ── Body ──────────────────────────────────────────────────── */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px', flex: 1 }}>
            <div className="spinner"></div>
          </div>
        ) : images.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', flex: 1 }}>
            <p style={{ color: 'var(--kahla-text-light)', marginBottom: '16px' }}>
              {activeTags.length > 0 || filter ? t.lm_no_match : t.lm_empty}
            </p>
            {(activeTags.length > 0 || filter) && (
              <button
                className="btn-secondary"
                onClick={() => { setFilter(null); setActiveTags([]); }}
                style={{ marginRight: '12px' }}
              >
                {t.lm_clear_filters}
              </button>
            )}
            <button className="btn-secondary" onClick={onClose}>
              {t.batch_close}
            </button>
          </div>
        ) : (
          <>
            {/* Selection status strip — sticks just above the grid on
                mobile so the founder always sees what's selected without
                scrolling all the way back up. */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: isNarrow ? '10px 18px' : '10px 14px',
              backgroundColor: 'var(--kahla-cream-light)',
              borderTop: isNarrow ? 'none' : '1px solid var(--kahla-border)',
              borderBottom: '1px solid var(--kahla-border)',
              flexShrink: 0,
            }}>
              <span style={{ fontSize: '12px', color: 'var(--kahla-text)' }}>
                {selectedIds.size === 0
                  ? fmt(t.lm_select_status_count, { count: images.length, plus: hasMore ? '+' : '' })
                  : fmt(selectedIds.size === 1 ? t.lm_select_status_one : t.lm_select_status_many, { count: selectedIds.size })}
              </span>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={selectAll}
                  data-testid="library-modal-select-all"
                  style={{
                    background: 'none', border: 'none',
                    fontSize: '11px', color: 'var(--kahla-taupe)',
                    cursor: 'pointer', textDecoration: 'underline',
                  }}
                >
                  {t.lm_select_visible}
                </button>
                {selectedIds.size > 0 && (
                  <button
                    onClick={clearSelection}
                    data-testid="library-modal-clear-selection"
                    style={{
                      background: 'none', border: 'none',
                      fontSize: '11px', color: 'var(--kahla-text-light)',
                      cursor: 'pointer', textDecoration: 'underline',
                    }}
                  >
                    {t.lm_clear}
                  </button>
                )}
              </div>
            </div>

            {/* Image Grid — fills available vertical space. On mobile
                this is the entire viewport between header and footer. */}
            <div
              ref={gridContainerRef}
              style={{
                flex: 1,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                minHeight: 0,
                padding: isNarrow ? '12px 18px 16px' : '14px 0 16px',
              }}
            >
              <div style={gridStyle}>
                {images.map((image) => {
                  const isSelected = selectedIds.has(image.id);

                  // Long-press detection. We start a timer on
                  // pointerdown; if it fires before pointerup, the
                  // preview opens and we set a flag so the subsequent
                  // pointerup-triggered click does NOT also toggle
                  // selection. Scroll movement cancels the gesture so
                  // it doesn't fire while the founder is just panning.
                  const startLongPress = (img) => {
                    longPressFiredRef.current = false;
                    clearTimeout(longPressTimerRef.current);
                    longPressTimerRef.current = setTimeout(() => {
                      longPressFiredRef.current = true;
                      openPreview(img);
                    }, LONG_PRESS_MS);
                  };
                  const cancelLongPress = () => {
                    clearTimeout(longPressTimerRef.current);
                  };

                  return (
                    <div
                      key={image.id}
                      data-testid={`library-image-${image.id}`}
                      style={{
                        cursor: 'pointer',
                        position: 'relative',
                        border: isSelected ? '3px solid var(--kahla-charcoal)' : '1px solid var(--kahla-border)',
                        aspectRatio: '1',
                        overflow: 'hidden',
                        transition: 'transform 0.15s, box-shadow 0.15s',
                        transform: isSelected ? 'scale(0.97)' : 'scale(1)',
                        boxShadow: isSelected ? '0 0 0 2px var(--kahla-cream), 0 4px 12px rgba(0,0,0,0.15)' : 'none',
                        touchAction: 'manipulation',
                      }}
                      onPointerDown={() => startLongPress(image)}
                      onPointerUp={cancelLongPress}
                      onPointerLeave={cancelLongPress}
                      onPointerCancel={cancelLongPress}
                      onContextMenu={(e) => e.preventDefault()}
                      onClick={() => {
                        // Suppress the click that immediately follows
                        // a fired long-press so the preview opens
                        // without also flipping the selection.
                        if (longPressFiredRef.current) {
                          longPressFiredRef.current = false;
                          return;
                        }
                        toggleSelection(image.id);
                      }}
                    >
                      {image.is_external && density !== 'tiny' && (
                        <div style={{
                          position: 'absolute',
                          top: '4px',
                          left: '4px',
                          backgroundColor: 'rgba(139, 119, 101, 0.9)',
                          color: 'white',
                          padding: '1px 5px',
                          fontSize: '8px',
                          letterSpacing: '0.03em',
                          textTransform: 'uppercase',
                          zIndex: 2,
                          fontWeight: '500',
                        }}>
                          {t.lm_ext_badge}
                        </div>
                      )}
                      <img
                        src={image.image_thumb || image.image_data || undefined}
                        alt={image.name || ''}
                        loading="lazy"
                        draggable={false}
                        style={{
                          width: '100%', height: '100%', objectFit: 'cover',
                          opacity: isSelected ? 0.9 : 1,
                          pointerEvents: 'none',
                        }}
                        onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f5f3f0" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%238b7765" font-size="10">Error</text></svg>'; }}
                      />
                      {image.tags && image.tags.length > 0 && density === 'comfortable' && (
                        <div style={{
                          position: 'absolute',
                          bottom: '4px',
                          left: '4px',
                          display: 'flex',
                          gap: '2px',
                          flexWrap: 'wrap',
                          maxWidth: 'calc(100% - 8px)',
                        }}>
                          {image.tags.slice(0, 2).map(tag => (
                            <span key={tag} style={{
                              fontSize: '8px', padding: '1px 4px',
                              backgroundColor: 'rgba(255,255,255,0.9)',
                              color: 'var(--kahla-text-light)',
                              maxWidth: '50px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>{tag}</span>
                          ))}
                          {image.tags.length > 2 && (
                            <span style={{ fontSize: '8px', backgroundColor: 'rgba(255,255,255,0.9)', padding: '1px 4px', color: 'var(--kahla-text-light)' }}>+{image.tags.length - 2}</span>
                          )}
                        </div>
                      )}

                      {/* Magnifier — small, calm affordance for opening
                          a full-size preview. Hidden on the tiniest
                          density to keep cells uncluttered; long-press
                          still works there. */}
                      {density !== 'tiny' && (
                        <button
                          type="button"
                          aria-label={t.lm_preview_aria}
                          title={t.lm_preview_aria}
                          data-testid={`library-image-preview-${image.id}`}
                          onClick={(e) => { e.stopPropagation(); openPreview(image); }}
                          onPointerDown={(e) => e.stopPropagation()}
                          style={{
                            position: 'absolute',
                            bottom: '6px',
                            right: '6px',
                            width: density === 'compact' ? '22px' : '26px',
                            height: density === 'compact' ? '22px' : '26px',
                            borderRadius: '50%',
                            background: 'rgba(255, 255, 255, 0.92)',
                            border: '1px solid rgba(28, 22, 14, 0.08)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            padding: 0,
                            zIndex: 3,
                            color: 'var(--kahla-charcoal)',
                            transition: 'background-color 0.15s',
                          }}
                        >
                          <svg
                            width={density === 'compact' ? 11 : 13}
                            height={density === 'compact' ? 11 : 13}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <circle cx="11" cy="11" r="7" />
                            <line x1="21" y1="21" x2="16.5" y2="16.5" />
                          </svg>
                        </button>
                      )}

                      <div style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        width: density === 'tiny' ? '16px' : '20px',
                        height: density === 'tiny' ? '16px' : '20px',
                        borderRadius: '50%',
                        backgroundColor: isSelected ? 'var(--kahla-charcoal)' : 'rgba(255,255,255,0.9)',
                        border: isSelected ? 'none' : '2px solid var(--kahla-border)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.15s',
                        zIndex: 3,
                      }}>
                        {isSelected && (
                          <span style={{ color: 'white', fontSize: density === 'tiny' ? '10px' : '12px', fontWeight: 'bold' }}>✓</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div ref={sentinelRef} style={{ height: '1px' }} />
              {loadingMore && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                  <div className="spinner" style={{ width: '24px', height: '24px' }}></div>
                </div>
              )}
              {hasMore && !loadingMore && (
                <div style={{ textAlign: 'center', padding: '12px' }}>
                  <button
                    onClick={loadMore}
                    className="btn-secondary"
                    style={{ fontSize: '12px', padding: '6px 16px' }}
                  >
                    {t.lm_load_more}
                  </button>
                </div>
              )}
            </div>

            {/* ── Sticky Footer ────────────────────────────────────── */}
            <div style={{
              flexShrink: 0,
              borderTop: '1px solid var(--kahla-border)',
              background: stickyBg,
              padding: isNarrow ? '12px 18px calc(12px + env(safe-area-inset-bottom))' : '14px 0 0',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}>
              {selectedIds.size > 0 && (
                <div style={{
                  display: 'flex',
                  flexDirection: isNarrow ? 'column' : 'row',
                  alignItems: isNarrow ? 'stretch' : 'center',
                  gap: '8px',
                }}>
                  <label style={{
                    fontSize: '11px',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'var(--kahla-text-light)',
                    margin: 0,
                    whiteSpace: 'nowrap',
                  }}>
                    {t.lm_section_label_for_selected}
                  </label>
                  <select
                    value={sectionLabel}
                    onChange={(e) => setSectionLabel(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      fontSize: '13px',
                      border: '1px solid var(--kahla-border)',
                      background: 'var(--kahla-white)',
                    }}
                    data-testid="library-modal-section-label"
                  >
                    <option value="">{t.lm_section_none}</option>
                    {sectionLabels.map(label => (
                      <option key={label} value={label}>{label}</option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px' }}>
                {!isNarrow && (
                  <button
                    className="btn-secondary"
                    style={{ flex: 1 }}
                    onClick={onClose}
                  >
                    {t.cancel}
                  </button>
                )}
                <button
                  className="btn-primary"
                  style={{ flex: isNarrow ? 1 : 1 }}
                  onClick={handleAdd}
                  disabled={selectedIds.size === 0}
                  data-testid="library-modal-add-btn"
                >
                  {selectedIds.size === 0
                    ? t.lm_select_images_btn
                    : fmt(selectedIds.size === 1 ? t.lm_add_one_to_board : t.lm_add_many_to_board, { count: selectedIds.size })}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Full-size preview overlay ─────────────────────────────────
          Opens on magnifier-tap or long-press of a thumbnail. Calm
          backdrop, image centered with object-fit:contain so nothing
          is cropped, and a small action row to (de)select without
          having to dismiss the preview first. */}
      {previewImage && (
        <div
          data-testid="library-modal-preview"
          onClick={closePreview}
          style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(12, 9, 6, 0.92)',
            zIndex: 1100,
            display: 'flex',
            flexDirection: 'column',
            padding: 'calc(20px + env(safe-area-inset-top)) 16px calc(20px + env(safe-area-inset-bottom))',
          }}
        >
          {/* Top bar: name + close — kept on the backdrop so the
              image dominates the view. */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              marginBottom: '16px',
              color: 'rgba(255, 247, 235, 0.92)',
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <p style={{
                margin: 0,
                fontFamily: 'Playfair Display, serif',
                fontSize: '15px',
                fontWeight: 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {previewImage.name || ''}
              </p>
              <p style={{
                margin: '2px 0 0',
                fontSize: '11px',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'rgba(255, 247, 235, 0.55)',
              }}>
                {previewImage.is_external ? t.lib_external_ref_badge : t.lib_uploaded_badge}
              </p>
            </div>
            <button
              type="button"
              onClick={closePreview}
              aria-label={t.lm_preview_close}
              data-testid="library-modal-preview-close"
              style={{
                background: 'rgba(255, 247, 235, 0.08)',
                border: '1px solid rgba(255, 247, 235, 0.18)',
                color: 'rgba(255, 247, 235, 0.92)',
                width: '36px', height: '36px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '18px',
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </div>

          {/* The image itself, contained. */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={previewImage.image_data || previewImage.image_thumb || ''}
              alt={previewImage.name || ''}
              draggable={false}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                userSelect: 'none',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
              }}
            />
          </div>

          {/* Tags + select toggle. Tapping the action does NOT close
              the overlay so the founder can keep evaluating siblings. */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              flexDirection: isNarrow ? 'column' : 'row',
              alignItems: isNarrow ? 'stretch' : 'center',
              gap: '10px',
              marginTop: '16px',
            }}
          >
            <div style={{
              flex: 1,
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px',
              minWidth: 0,
            }}>
              {previewImage.tags && previewImage.tags.length > 0 ? (
                previewImage.tags.map((tag) => (
                  <span key={tag} style={{
                    fontSize: '11px',
                    padding: '3px 8px',
                    background: 'rgba(255, 247, 235, 0.08)',
                    color: 'rgba(255, 247, 235, 0.78)',
                    border: '1px solid rgba(255, 247, 235, 0.14)',
                  }}>{tag}</span>
                ))
              ) : (
                <span style={{
                  fontSize: '12px',
                  color: 'rgba(255, 247, 235, 0.45)',
                  fontStyle: 'italic',
                  fontFamily: '"Cormorant Garamond", serif',
                }}>{t.lm_preview_no_tags}</span>
              )}
            </div>
            <button
              type="button"
              data-testid="library-modal-preview-select"
              onClick={() => toggleSelection(previewImage.id)}
              style={{
                padding: '10px 22px',
                fontSize: '12px',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                border: '1px solid rgba(255, 247, 235, 0.32)',
                background: selectedIds.has(previewImage.id)
                  ? 'rgba(255, 247, 235, 0.92)'
                  : 'transparent',
                color: selectedIds.has(previewImage.id)
                  ? 'var(--kahla-charcoal)'
                  : 'rgba(255, 247, 235, 0.92)',
                fontFamily: 'inherit',
                transition: 'background-color 0.18s, color 0.18s',
              }}
            >
              {selectedIds.has(previewImage.id) ? t.lm_preview_deselect : t.lm_preview_select}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default LibraryModal;
